/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations } from '@/shared/constants/locales';

type Language = keyof typeof translations;
type Theme = 'light' | 'dark' | 'system';

const VALID_LANGUAGES: Language[] = ['en', 'es', 'zh', 'hi', 'fr', 'ar', 'vi'];
const VALID_THEMES: Theme[] = ['light', 'dark', 'system'];

const isValidLanguage = (val: unknown): val is Language =>
  typeof val === 'string' && VALID_LANGUAGES.includes(val as Language);

const isValidTheme = (val: unknown): val is Theme =>
  typeof val === 'string' && VALID_THEMES.includes(val as Theme);

interface SettingsContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  t: (key: keyof typeof translations['en']) => string;
  dir: 'ltr' | 'rtl';
  auroraEnabled: boolean;
  setAurora: (enabled: boolean) => void;
  toggleAurora: () => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    try {
      const stored = localStorage.getItem('voxpop-lang');
      return isValidLanguage(stored) ? stored : 'en';
    } catch {
      return 'en';
    }
  });

  const [theme, setThemeState] = useState<Theme>(() => {
    try {
      const stored = localStorage.getItem('voxpop-theme');
      return isValidTheme(stored) ? stored : 'system';
    } catch {
      return 'system';
    }
  });

  useEffect(() => {
    localStorage.setItem('voxpop-lang', language);
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  useEffect(() => {
    localStorage.setItem('voxpop-theme', theme);
    const root = document.documentElement;

    if (theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  const setLanguage = (lang: Language) => {
    if (isValidLanguage(lang)) {
      setLanguageState(lang);
    } else {
      console.error('Invalid language provided:', lang);
    }
  };

  const setTheme = (t: Theme) => {
    if (isValidTheme(t)) {
      setThemeState(t);
    } else {
      console.error('Invalid theme provided:', t);
    }
  };

  const toggleTheme = () => {
    setThemeState(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const t = (key: keyof typeof translations['en']): string => {
    return translations[language][key] || translations['en'][key] || key;
  };

  const dir = language === 'ar' ? 'rtl' : 'ltr';

  // Aurora State
  const [auroraEnabled, setAuroraEnabled] = useState(() => {
    try {
      const stored = localStorage.getItem('voxpop-aurora');
      return stored !== null ? stored === 'true' : true;
    } catch {
      return true;
    }
  });

  const setAurora = (enabled: boolean) => {
    setAuroraEnabled(enabled);
    localStorage.setItem('voxpop-aurora', String(enabled));
  };

  const toggleAurora = () => {
    setAuroraEnabled(prev => {
      const current = !prev;
      localStorage.setItem('voxpop-aurora', String(current));
      return current;
    });
  };

  return (
    <SettingsContext.Provider value={{ language, setLanguage, theme, setTheme, toggleTheme, t, dir, auroraEnabled, toggleAurora, setAurora }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const LANGUAGES: { code: Language; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'zh', label: '中文' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'fr', label: 'Français' },
  { code: 'ar', label: 'العربية' },
  { code: 'vi', label: 'Tiếng Việt' }
];
